import { useCallback, useEffect, useRef, useState } from "react";
import {
  ArrowRight, Circle, Minus, Pen, RotateCcw, RotateCw,
  Save, Square, Type, X,
} from "lucide-react";

const COLORS  = ["#ffffff","#000000","#ef4444","#f97316","#f59e0b","#22c55e","#3b82f6","#8b5cf6","#ec4899","#42f6d7"];
const STROKES = [2, 4, 8, 14];
const TOOLS   = [
  { id: "pen",    Icon: Pen,          label: "Pen"    },
  { id: "line",   Icon: Minus,        label: "Line"   },
  { id: "arrow",  Icon: ArrowRight,   label: "Arrow"  },
  { id: "rect",   Icon: Square,       label: "Rect"   },
  { id: "circle", Icon: Circle,       label: "Circle" },
  { id: "text",   Icon: Type,         label: "Text"   },
];

export default function AnnotationEditor({ blob, onSave, onCancel }) {
  const canvasRef    = useRef(null);
  const overlayRef   = useRef(null);
  const imgRef       = useRef(null);
  const histRef      = useRef([]);
  const redoStackRef = useRef([]);
  const drawing      = useRef(false);
  const startPos     = useRef({ x: 0, y: 0 });
  const textInputRef = useRef(null);

  const [tool,     setTool]     = useState("pen");
  const [color,    setColor]    = useState("#ef4444");
  const [stroke,   setStroke]   = useState(4);
  const [canUndo,  setCanUndo]  = useState(false);
  const [canRedo,  setCanRedo]  = useState(false);
  const [textMode, setTextMode] = useState(null);   // { x, y } in canvas %
  const [textVal,  setTextVal]  = useState("");
  const [imgUrl,   setImgUrl]   = useState("");
  const [saving,   setSaving]   = useState(false);

  useEffect(() => {
    if (!blob) return;
    const u = URL.createObjectURL(blob);
    setImgUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [blob]);

  const onImgLoad = useCallback(() => {
    const img = imgRef.current;
    const c   = canvasRef.current;
    const ov  = overlayRef.current;
    if (!img || !c || !ov) return;
    c.width  = ov.width  = img.naturalWidth;
    c.height = ov.height = img.naturalHeight;
    histRef.current      = [];
    redoStackRef.current = [];
    setCanUndo(false); setCanRedo(false);
  }, []);

  const snapshot = useCallback(() => {
    const c = canvasRef.current;
    if (!c) return;
    histRef.current.push(c.toDataURL());
    redoStackRef.current = [];
    setCanUndo(true); setCanRedo(false);
  }, []);

  const restoreImg = useCallback((dataUrl, cb) => {
    const c   = canvasRef.current;
    const ctx = c.getContext("2d");
    const im  = new Image();
    im.onload = () => { ctx.clearRect(0, 0, c.width, c.height); ctx.drawImage(im, 0, 0); cb?.(); };
    im.src = dataUrl;
  }, []);

  const undo = useCallback(() => {
    if (!histRef.current.length) return;
    redoStackRef.current.push(canvasRef.current.toDataURL());
    restoreImg(histRef.current.pop(), () => {
      setCanUndo(histRef.current.length > 0);
      setCanRedo(true);
    });
  }, [restoreImg]);

  const redo = useCallback(() => {
    if (!redoStackRef.current.length) return;
    histRef.current.push(canvasRef.current.toDataURL());
    restoreImg(redoStackRef.current.pop(), () => {
      setCanUndo(true);
      setCanRedo(redoStackRef.current.length > 0);
    });
  }, [restoreImg]);

  const pos = (e, canvas) => {
    const r  = canvas.getBoundingClientRect();
    const sx = canvas.width  / r.width;
    const sy = canvas.height / r.height;
    const cl = e.touches ? e.touches[0].clientX : e.clientX;
    const ct = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: (cl - r.left) * sx, y: (ct - r.top) * sy };
  };

  const onPointerDown = useCallback((e) => {
    const c = canvasRef.current;
    const p = pos(e, c);
    if (tool === "text") {
      const r  = c.getBoundingClientRect();
      const px = ((e.touches ? e.touches[0].clientX : e.clientX) - r.left) / r.width  * 100;
      const py = ((e.touches ? e.touches[0].clientY : e.clientY) - r.top)  / r.height * 100;
      setTextMode({ px, py, cx: p.x, cy: p.y });
      setTextVal("");
      setTimeout(() => textInputRef.current?.focus(), 30);
      return;
    }
    e.preventDefault();
    drawing.current = true;
    startPos.current = p;
    if (tool === "pen") {
      snapshot();
      const ctx = c.getContext("2d");
      ctx.beginPath(); ctx.moveTo(p.x, p.y);
      ctx.strokeStyle = color; ctx.lineWidth = stroke;
      ctx.lineCap = "round"; ctx.lineJoin = "round";
    }
  }, [tool, color, stroke, snapshot]);

  const onPointerMove = useCallback((e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const c   = canvasRef.current;
    const ov  = overlayRef.current;
    const p   = pos(e, c);
    const ctx = c.getContext("2d");

    if (tool === "pen") {
      ctx.lineTo(p.x, p.y); ctx.stroke(); return;
    }

    const oCtx = ov.getContext("2d");
    oCtx.clearRect(0, 0, ov.width, ov.height);
    oCtx.strokeStyle = color; oCtx.lineWidth = stroke; oCtx.lineCap = "round";
    const { x: sx, y: sy } = startPos.current;

    if (tool === "line") {
      oCtx.beginPath(); oCtx.moveTo(sx, sy); oCtx.lineTo(p.x, p.y); oCtx.stroke();
    } else if (tool === "rect") {
      oCtx.beginPath(); oCtx.strokeRect(sx, sy, p.x - sx, p.y - sy);
    } else if (tool === "circle") {
      const rx = Math.abs(p.x - sx) / 2, ry = Math.abs(p.y - sy) / 2;
      oCtx.beginPath();
      oCtx.ellipse(sx + (p.x-sx)/2, sy + (p.y-sy)/2, rx, ry, 0, 0, Math.PI*2);
      oCtx.stroke();
    } else if (tool === "arrow") {
      const angle = Math.atan2(p.y - sy, p.x - sx);
      const len   = 12 + stroke * 2.5;
      oCtx.beginPath(); oCtx.moveTo(sx, sy); oCtx.lineTo(p.x, p.y); oCtx.stroke();
      oCtx.beginPath();
      oCtx.moveTo(p.x, p.y);
      oCtx.lineTo(p.x - len * Math.cos(angle - 0.4), p.y - len * Math.sin(angle - 0.4));
      oCtx.lineTo(p.x - len * Math.cos(angle + 0.4), p.y - len * Math.sin(angle + 0.4));
      oCtx.closePath(); oCtx.fillStyle = color; oCtx.fill();
    }
  }, [tool, color, stroke]);

  const onPointerUp = useCallback(() => {
    if (!drawing.current) return;
    drawing.current = false;
    if (tool === "pen") return;
    const c  = canvasRef.current;
    const ov = overlayRef.current;
    snapshot();
    c.getContext("2d").drawImage(ov, 0, 0);
    ov.getContext("2d").clearRect(0, 0, ov.width, ov.height);
  }, [tool, snapshot]);

  const commitText = useCallback(() => {
    if (!textMode || !textVal.trim()) { setTextMode(null); return; }
    const c   = canvasRef.current;
    const ctx = c.getContext("2d");
    const sc  = Math.max(1, c.width / 500);
    const fs  = Math.round(stroke * 7 * sc);
    snapshot();
    ctx.font         = `bold ${fs}px -apple-system, BlinkMacSystemFont, sans-serif`;
    ctx.fillStyle    = color;
    ctx.shadowColor  = "rgba(0,0,0,0.8)";
    ctx.shadowBlur   = 5;
    ctx.fillText(textVal, textMode.cx, textMode.cy);
    ctx.shadowBlur   = 0;
    setTextMode(null); setTextVal("");
  }, [textMode, textVal, color, stroke, snapshot]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const photo = imgRef.current;
      const ann   = canvasRef.current;
      if (!photo || !ann) return;
      const final = document.createElement("canvas");
      final.width  = photo.naturalWidth;
      final.height = photo.naturalHeight;
      const ctx = final.getContext("2d");
      ctx.drawImage(photo, 0, 0);
      ctx.drawImage(ann,   0, 0);
      final.toBlob((b) => (b ? onSave(b) : onCancel()), "image/jpeg", 0.93);
    } finally {
      setSaving(false);
    }
  }, [onSave, onCancel]);

  return (
    <div className="ann-editor">
      {/* Header */}
      <header className="ann-header safe-top">
        <button className="ann-hdr-btn" onClick={onCancel}><X size={20} /></button>
        <span className="ann-hdr-title">Annotate</span>
        <div className="ann-hdr-right">
          <button className="ann-hdr-btn" onClick={undo} disabled={!canUndo} title="Undo">
            <RotateCcw size={17} />
          </button>
          <button className="ann-hdr-btn" onClick={redo} disabled={!canRedo} title="Redo">
            <RotateCw size={17} />
          </button>
          <button className="ann-save-btn" onClick={handleSave} disabled={saving}>
            {saving ? "…" : <><Save size={14} /> Save</>}
          </button>
        </div>
      </header>

      {/* Canvas area */}
      <div className="ann-canvas-area">
        {imgUrl && (
          <img
            ref={imgRef}
            src={imgUrl}
            className="ann-bg-img"
            alt=""
            onLoad={onImgLoad}
            draggable={false}
          />
        )}
        <canvas
          ref={canvasRef}
          className="ann-canvas"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
          style={{ touchAction: "none" }}
        />
        <canvas
          ref={overlayRef}
          className="ann-canvas ann-canvas-overlay"
          style={{ pointerEvents: "none" }}
        />

        {/* Floating text input */}
        {textMode && (
          <div
            className="ann-text-float"
            style={{ left: `${textMode.px}%`, top: `${textMode.py}%` }}
          >
            <input
              ref={textInputRef}
              className="ann-text-field"
              value={textVal}
              onChange={(e) => setTextVal(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter")  commitText();
                if (e.key === "Escape") setTextMode(null);
              }}
              placeholder="Type…"
            />
            <button className="ann-text-ok" onClick={commitText}>✓</button>
          </div>
        )}
      </div>

      {/* Toolbar */}
      <div className="ann-toolbar safe-bottom">
        {/* Tool buttons */}
        <div className="ann-tool-row">
          {TOOLS.map(({ id, Icon, label }) => (
            <button
              key={id}
              className={`ann-tool${tool === id ? " on" : ""}`}
              onClick={() => setTool(id)}
              title={label}
            >
              <Icon size={17} />
            </button>
          ))}
        </div>

        {/* Color swatches */}
        <div className="ann-color-row">
          {COLORS.map((c) => (
            <button
              key={c}
              className={`ann-color${color === c ? " on" : ""}`}
              style={{ background: c }}
              onClick={() => setColor(c)}
            />
          ))}
        </div>

        {/* Stroke sizes */}
        <div className="ann-stroke-row">
          {STROKES.map((s) => (
            <button
              key={s}
              className={`ann-sw${stroke === s ? " on" : ""}`}
              onClick={() => setStroke(s)}
            >
              <span className="ann-sw-bar" style={{ height: s + 1, background: color }} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
